import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  Divider,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import {
  FiDownload,
  FiUpload,
  FiFileText,
} from 'react-icons/fi';
import { useGetBulkUploadTemplate, useBulkUploadEmployees, downloadBulkUploadTemplate } from '../services/employeeService';

const BulkUpload = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const fileInputRef = useRef();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Template download hook
  const {
    bulkUploadTemplateData,
  } = useGetBulkUploadTemplate({ enabled: false });

  // Bulk upload hook
  const {
    bulkUploadPayload,
    bulkUploadIsLoading,
    bulkUploadError,
  } = useBulkUploadEmployees((results) => {
    setUploadResults(results);
    setShowResults(true);
    if (onSuccess) onSuccess(results);
  });

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadBulkUploadTemplate();
      
      toast({
        title: "Template downloaded successfully!",
        description: "Please fill in the template with your employee data.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to download template",
        description: "Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        toast({
          title: "File selected successfully!",
          description: `${file.name} is ready for upload.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Invalid file type",
          description: "Please select a CSV file.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a CSV file to upload.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    bulkUploadPayload(formData);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResults(null);
    setShowResults(false);
    onClose();
  };



  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <FiUpload />
            <Text>Bulk Upload Employees</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!showResults ? (
            <VStack spacing={6} align="stretch">
              {/* Configuration Warning */}
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Configuration Required!</AlertTitle>
                  <AlertDescription>
                    Before uploading employees, please ensure you have configured the following:
                    <Text mt={2} fontWeight="bold">
                      • Locations (get ObjectId from Configuration → Locations)
                    </Text>
                    <Text fontWeight="bold">
                      • Departments (get ObjectId from Configuration → Departments)
                    </Text>
                    <Text fontWeight="bold">
                      • Reporting Seniors (get ObjectId from Configuration → Reporting Seniors)
                    </Text>
                    <Text fontWeight="bold">
                      • Shifts (get ObjectId from Configuration → Shifts)
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Template Download Section */}
              <Card>
                <CardHeader>
                  <HStack spacing={3}>
                    <FiFileText />
                    <Heading size="md">Step 1: Download Template</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="sm" color="gray.600">
                      Download the CSV template with the correct format and available configuration IDs.
                    </Text>
                                         <Button
                       leftIcon={<FiDownload />}
                       colorScheme="blue"
                       onClick={handleDownloadTemplate}
                       isLoading={isDownloading}
                       loadingText="Downloading..."
                     >
                       Download Template
                     </Button>
                    
                                         {bulkUploadTemplateData && (
                       <Box>
                         <Text fontSize="sm" fontWeight="bold" mb={2}>
                           Available Configuration IDs:
                         </Text>
                         <SimpleGrid columns={2} spacing={4}>
                           <Box>
                             <Text fontSize="xs" fontWeight="bold" color="blue.600">Locations:</Text>
                             <Text fontSize="xs" color="gray.600">
                               {bulkUploadTemplateData.available_locations?.map(loc => 
                                 `${loc.name}: ${loc.id}`
                               ).join(', ') || 'None available'}
                             </Text>
                           </Box>
                           <Box>
                             <Text fontSize="xs" fontWeight="bold" color="green.600">Departments:</Text>
                             <Text fontSize="xs" color="gray.600">
                               {bulkUploadTemplateData.available_departments?.map(dept => 
                                 `${dept.name}: ${dept.id}`
                               ).join(', ') || 'None available'}
                             </Text>
                           </Box>
                           <Box>
                             <Text fontSize="xs" fontWeight="bold" color="purple.600">Reporting Seniors:</Text>
                             <Text fontSize="xs" color="gray.600">
                               {bulkUploadTemplateData.available_reporting_seniors?.map(rep => 
                                 `${rep.name}: ${rep.id}`
                               ).join(', ') || 'None available'}
                             </Text>
                           </Box>
                           <Box>
                             <Text fontSize="xs" fontWeight="bold" color="orange.600">Shifts:</Text>
                             <Text fontSize="xs" color="gray.600">
                               {bulkUploadTemplateData.available_shifts?.map(shift => 
                                 `${shift.name}: ${shift.id}`
                               ).join(', ') || 'None available'}
                             </Text>
                           </Box>
                         </SimpleGrid>
                       </Box>
                     )}
                  </VStack>
                </CardBody>
              </Card>

              <Divider />

              {/* File Upload Section */}
              <Card>
                <CardHeader>
                  <HStack spacing={3}>
                    <FiUpload />
                    <Heading size="md">Step 2: Upload CSV File</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="sm" color="gray.600">
                      Select your filled CSV file to upload employee data.
                    </Text>
                    
                    <Box
                      border="2px dashed"
                      borderColor={selectedFile ? "green.200" : "gray.200"}
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                      bg={selectedFile ? "green.50" : "gray.50"}
                      cursor="pointer"
                      onClick={() => fileInputRef.current?.click()}
                      _hover={{ borderColor: "blue.300", bg: "blue.50" }}
                    >
                      <VStack spacing={2}>
                        <FiUpload size={24} color={selectedFile ? "#38A169" : "#718096"} />
                        <Text fontSize="sm" fontWeight="medium">
                          {selectedFile ? selectedFile.name : "Click to select CSV file"}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {selectedFile ? "File selected successfully" : "Only CSV files are supported"}
                        </Text>
                      </VStack>
                    </Box>

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".csv"
                      style={{ display: 'none' }}
                    />

                    <Button
                      leftIcon={<FiUpload />}
                      colorScheme="green"
                      onClick={handleUpload}
                      isLoading={bulkUploadIsLoading}
                      loadingText="Uploading..."
                      isDisabled={!selectedFile}
                    >
                      Upload Employees
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              {bulkUploadError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertTitle>Upload Failed!</AlertTitle>
                  <AlertDescription>{bulkUploadError}</AlertDescription>
                </Alert>
              )}
            </VStack>
          ) : (
            // Results Section
            <VStack spacing={6} align="stretch">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <AlertTitle>Upload Completed!</AlertTitle>
                <AlertDescription>
                  The bulk upload has been processed. Check the results below.
                </AlertDescription>
              </Alert>

              {/* Summary Statistics */}
              <SimpleGrid columns={3} spacing={4}>
                <Stat>
                  <StatLabel>Total Processed</StatLabel>
                  <StatNumber color="blue.600">
                    {uploadResults?.total_processed || 0}
                  </StatNumber>
                  <StatHelpText>Total rows processed</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Successfully Added</StatLabel>
                  <StatNumber color="green.600">
                    {uploadResults?.success_count || 0}
                  </StatNumber>
                  <StatHelpText>Employees added successfully</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Failed</StatLabel>
                  <StatNumber color="red.600">
                    {uploadResults?.failure_count || 0}
                  </StatNumber>
                  <StatHelpText>Rows with errors</StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* Progress Bar */}
              {uploadResults?.total_processed > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Success Rate
                  </Text>
                  <Progress
                    value={(uploadResults?.success_count / uploadResults?.total_processed) * 100}
                    colorScheme="green"
                    size="lg"
                    borderRadius="md"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {Math.round((uploadResults?.success_count / uploadResults?.total_processed) * 100)}% success rate
                  </Text>
                </Box>
              )}

              {/* Error Details */}
              {uploadResults?.errors && uploadResults.errors.length > 0 && (
                <Box>
                  <Text fontSize="md" fontWeight="bold" mb={3}>
                    Error Details
                  </Text>
                  <Box maxH="300px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Row</Th>
                          <Th>Field</Th>
                          <Th>Error</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {uploadResults.errors.map((error, index) => (
                          <Tr key={index}>
                            <Td>
                              <Badge colorScheme="red" variant="subtle">
                                Row {error.row || index + 1}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm" fontWeight="medium">
                                {error.field || 'N/A'}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="red.600">
                                {error.message || error.error || 'Unknown error'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}

              {/* Success Details */}
              {uploadResults?.success_details && uploadResults.success_details.length > 0 && (
                <Box>
                  <Text fontSize="md" fontWeight="bold" mb={3}>
                    Successfully Added Employees
                  </Text>
                  <Box maxH="200px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Employee ID</Th>
                          <Th>Name</Th>
                          <Th>Email</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {uploadResults.success_details.map((employee, index) => (
                          <Tr key={index}>
                            <Td>
                              <Badge colorScheme="green" variant="subtle">
                                {employee.employee_id}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm">
                                {employee.first_name} {employee.last_name}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600">
                                {employee.email}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            {showResults && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowResults(false);
                  setSelectedFile(null);
                  setUploadResults(null);
                }}
              >
                Upload Another File
              </Button>
            )}
            <Button onClick={handleClose}>
              {showResults ? 'Close' : 'Cancel'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkUpload; 